import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Target, 
  Eye, 
  Heart, 
  Shield, 
  Zap, 
  Users,
  Globe,
  Award
} from 'lucide-react';

export const About = () => {
  const values = [
    {
      icon: <Shield className="w-6 h-6" strokeWidth={1.5} />,
      title: "Security First",
      description: "We prioritize the security of your transactions and data above all else."
    },
    {
      icon: <Zap className="w-6 h-6" strokeWidth={1.5} />,
      title: "Reliability",
      description: "Our infrastructure is built to be available 24/7, ensuring you never miss a transaction."
    },
    {
      icon: <Heart className="w-6 h-6" strokeWidth={1.5} />,
      title: "Customer Focus",
      description: "We build products based on real problems faced by African businesses."
    },
    {
      icon: <Globe className="w-6 h-6" strokeWidth={1.5} />,
      title: "Local Expertise",
      description: "Deep understanding of African markets and mobile money ecosystems."
    }
  ];

  const milestones = [
    { year: "2022", title: "Founded", description: "TrustBridge Technologies established in Monrovia" },
    { year: "2023", title: "FlowLedger Launch", description: "Transaction tracking platform goes live" },
    { year: "2024", title: "SIMVault Beta", description: "Secure SIM hosting service introduced" },
    { year: "2025", title: "Regional Expansion", description: "Expanding services across West Africa" }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block animate-fade-in">
              About Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6 animate-fade-in-up">
              Building Trust in African{' '}
              <span className="gradient-text">Financial Infrastructure</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed animate-fade-in-up delay-100">
              We're on a mission to provide secure, reliable transaction infrastructure 
              for businesses across Liberia and Africa.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white" data-testid="story-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block">
                Our Story
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Born from Real Challenges
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  TrustBridge Technologies was founded with a clear vision: to solve the real 
                  challenges businesses face when managing mobile money transactions in Liberia.
                </p>
                <p>
                  We saw business owners struggling to track payments, losing sales due to 
                  unconfirmed transactions, and diaspora entrepreneurs unable to monitor their 
                  operations from abroad.
                </p>
                <p>
                  Our team of engineers and fintech experts came together to build 
                  enterprise-grade solutions that are accessible to businesses of all sizes.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-sky-100 rounded-3xl transform rotate-3" />
              <div className="relative bg-[#0A1628] rounded-3xl p-10 text-white">
                <div className="grid grid-cols-2 gap-8">
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">500+</div>
                    <div className="text-slate-400">Businesses</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">50K+</div>
                    <div className="text-slate-400">Transactions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">99.9%</div>
                    <div className="text-slate-400">Uptime</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-2">24/7</div>
                    <div className="text-slate-400">Support</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-slate-50" data-testid="mission-section">
        <div className="container-main">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-10 border border-slate-100">
              <div className="icon-container-lg mb-6 bg-sky-50 text-[#0077B6]">
                <Target className="w-8 h-8" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">Our Mission</h3>
              <p className="text-slate-600 leading-relaxed">
                To empower African businesses with secure, reliable transaction infrastructure 
                that enables them to operate with confidence and grow without limits.
              </p>
            </div>
            
            <div className="bg-white rounded-3xl p-10 border border-slate-100">
              <div className="icon-container-lg mb-6 bg-slate-100 text-slate-700">
                <Eye className="w-8 h-8" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">Our Vision</h3>
              <p className="text-slate-600 leading-relaxed">
                To be the most trusted financial technology infrastructure provider in Africa, 
                bridging the gap between mobile money and modern business operations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-white" data-testid="values-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              What Drives Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="icon-container mx-auto mb-6 bg-sky-50 text-[#0077B6]">
                  {value.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding bg-slate-50" data-testid="timeline-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block">
              Our Journey
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900">
              Milestones
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-200" />
              
              {milestones.map((milestone, index) => (
                <div 
                  key={index}
                  className="relative pl-20 pb-12 last:pb-0"
                  data-testid={`milestone-${index}`}
                >
                  <div className="absolute left-5 top-0 w-6 h-6 rounded-full bg-[#0077B6] border-4 border-white shadow" />
                  <div className="bg-white rounded-xl p-6 border border-slate-100">
                    <span className="text-sm font-semibold text-[#0077B6]">{milestone.year}</span>
                    <h4 className="text-lg font-semibold text-slate-900 mt-1">{milestone.title}</h4>
                    <p className="text-slate-600 mt-1">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-white" data-testid="team-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block">
              Our Team
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Built by Experts
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our team combines deep fintech expertise with local market knowledge 
              to build solutions that truly work for African businesses.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-slate-100 mx-auto mb-4 flex items-center justify-center">
                <Users className="w-10 h-10 text-slate-400" strokeWidth={1.5} />
              </div>
              <h4 className="font-semibold text-slate-900">Engineering</h4>
              <p className="text-sm text-slate-600">Building robust infrastructure</p>
            </div>
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-slate-100 mx-auto mb-4 flex items-center justify-center">
                <Shield className="w-10 h-10 text-slate-400" strokeWidth={1.5} />
              </div>
              <h4 className="font-semibold text-slate-900">Security</h4>
              <p className="text-sm text-slate-600">Protecting your assets</p>
            </div>
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-slate-100 mx-auto mb-4 flex items-center justify-center">
                <Award className="w-10 h-10 text-slate-400" strokeWidth={1.5} />
              </div>
              <h4 className="font-semibold text-slate-900">Operations</h4>
              <p className="text-sm text-slate-600">Ensuring reliability</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0A1628]" data-testid="about-cta-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
              Join Our Journey
            </h2>
            <p className="text-slate-300 text-lg mb-10 leading-relaxed">
              Partner with us to transform how African businesses manage their transactions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=demo">
                <Button className="btn-accent text-base px-8 py-6" data-testid="about-cta-demo-btn">
                  Request Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10 rounded-full px-8 py-6 text-base" data-testid="about-cta-contact-btn">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
